'use client'
import { ComponentProps, ReactNode } from 'react'
import styled, { css } from 'styled-components'

import { TypeStyle, focusOutline } from '@syconium/little-miss-figgy'

export const Label = styled(TypeStyle.BodyNano).attrs({
  asTag: 'label',
  fontWeight: 700,
})<{ $required: boolean }>`
  letter-spacing: 1.6px;
  ${({ $required }) =>
    $required &&
    css`
      &::after {
        content: '*';
        color: ${o => o.theme.color.text.critical.on.background};
        font-size: 15px;
      }
    `}
`

type FormLabelProps = {
  /** Whether or not the label should indicate that the form control is required. */
  required?: boolean
  /** An optional adornment element that may be displayed in alignment with the form control label. */
  adornment?: ReactNode
} & ComponentProps<typeof TypeStyle.BodyNano>

/**
 * Primitive form label element that optionally allows for additional adornment content.
 *
 * This component may be provided to the `render` prop of `Ariakit.FormLabel` in the context of an `Ariakit` form.
 */
export const FormLabel = styled(({ required, adornment, className, ...rest }: FormLabelProps) => {
  return (
    <div className={className}>
      <Label $required={!!required} textTransform='uppercase' {...rest} />
      {adornment}
    </div>
  )
})`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${o => o.theme.spacing(2)};
`

/** Display the various components of a form control (i.e. label, input, error message) with consistent vertical rhythm. */
export const FormControlWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(2)};
`

/** Primitive input component that properly handles focus/invalid/disabled states. */
export const FormInput = styled.input`
  ${TypeStyle.css.bodySmall}
  color: ${o => o.theme.color.input.empty.content.on.background};
  background: ${o => o.theme.color.input.empty.fill.on.background};
  border-radius: 4px;
  border: 1px solid ${o => o.theme.color.input.empty.border.on.background};
  box-sizing: border-box;
  font-weight: 600;
  height: 42px;
  padding: ${o => o.theme.spacing(2, 4)};
  width: 100%;

  &:not(:placeholder-shown):not(:disabled):not([aria-invalid='true']):not(:focus) {
    border: 1px solid ${o => o.theme.color.stroke.neutral.border.interaction.primary.on.background};
  }

  &[aria-invalid='true'] {
    border: 1px solid ${o => o.theme.color.input.invalid.empty.border.on.background};
  }

  &:focus {
    ${focusOutline}
  }

  &:disabled {
    border-color: ${o => o.theme.color.input.disabled.empty.border.on.background};
    color: ${o => o.theme.color.input.disabled.empty.content.on.background};
  }

  &::placeholder {
    color: ${o => o.theme.color.text.secondary.on.background};
    ${TypeStyle.css.bodySmall}
    font-weight: 600;
    opacity: 0.8;
  }

  &:autofill {
    ${TypeStyle.css.bodySmall};
    font-weight: 600;
  }
`

/** Primitive component for displaying field or form level error messages. */
export const ErrorMessage = styled(TypeStyle.BodyMicro).attrs({
  color: 'critical',
  role: 'alert',
})`
  /* 'Ariakit.FormError' renders error elements even when there is no error message to display. */
  &:empty {
    display: none;
  }
`
